<script setup>
import { useApi } from '@/utils/api'
import { requiredValidator } from '@core/utils/validators'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Ticket',
  },
})

const { useAbility } = await import('@casl/vue')
const ability = useAbility()

const route = useRoute('support-id')

const PRIORITY_META = {
  low: { label: 'Rendah', color: 'secondary' },
  medium: { label: 'Sedang', color: 'info' },
  high: { label: 'Tinggi', color: 'warning' },
  urgent: { label: 'Urgent', color: 'error' },
}

const STATUS_META = {
  open: { label: 'Terbuka', color: 'info' },
  in_progress: { label: 'Diproses', color: 'warning' },
  resolved: { label: 'Selesai', color: 'success' },
  closed: { label: 'Ditutup', color: 'secondary' },
}

const CATEGORY_LABELS = {
  billing: 'Tagihan',
  technical: 'Teknis',
  feature_request: 'Request Fitur',
  other: 'Lainnya',
}

const formatDateTime = iso =>
  iso ? new Date(iso).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'

const ticket = ref(null)
const loading = ref(true)
const fetchError = ref('')

const fetchTicket = async () => {
  loading.value = true
  fetchError.value = ''
  try {
    const response = await useApi(`/api/v1/tickets/${route.params.id}`).get()
    const body = response?.data ?? response

    ticket.value = body?.data ?? body
  } catch (err) {
    console.error('[Support] fetch ticket error', err)
    fetchError.value = 'Tiket tidak ditemukan, atau bukan milik toko Anda.'
  } finally {
    loading.value = false
  }
}

onMounted(fetchTicket)

// Riwayat percakapan = tiket awal (deskripsi) + semua balasan, urut
// kronologis -- backend sudah urutkan replies, di sini cuma menyusun jadi
// 1 timeline gabungan (§2.1 poin 4 addendum).
const conversation = computed(() => {
  if (!ticket.value)
    return []

  const opening = {
    id: `ticket-${ticket.value.id}`,
    message: ticket.value.description,
    is_from_platform_admin: false,
    user: ticket.value.creator,
    created_at: ticket.value.created_at,
    isOpening: true,
  }

  return [opening, ...(ticket.value.replies ?? [])]
})

// ── Reply ────────────────────────────────────────────────────────────────
const replyMessage = ref('')
const replyLoading = ref(false)
const replyError = ref('')

const submitReply = async () => {
  if (!replyMessage.value.trim())
    return

  replyLoading.value = true
  replyError.value = ''

  try {
    await useApi(`/api/v1/tickets/${route.params.id}/replies`).post({
      body: { message: replyMessage.value },
    })

    replyMessage.value = ''
    await fetchTicket()
  } catch (err) {
    const data = err?.data ?? err?.response?._data

    replyError.value = data?.message ?? 'Gagal mengirim balasan.'
  } finally {
    replyLoading.value = false
  }
}
</script>

<template>
  <div>
    <VBreadcrumbs
      :items="[
        { title: 'Bantuan / Support', to: { name: 'support' } },
        { title: ticket?.subject ?? 'Detail Tiket', disabled: true },
      ]"
      class="px-0 mb-4"
    />

    <VCard
      v-if="loading"
      class="pa-10 text-center"
    >
      <VProgressCircular
        indeterminate
        color="primary"
      />
    </VCard>

    <VCard
      v-else-if="fetchError"
      class="pa-6"
    >
      <VAlert
        type="error"
        variant="tonal"
      >
        {{ fetchError }}
      </VAlert>
    </VCard>

    <template v-else-if="ticket">
      <VCard class="mb-4">
        <VCardText class="pa-6">
          <div class="d-flex align-center justify-space-between flex-wrap gap-3 mb-3">
            <h5 class="text-h5 font-weight-bold mb-0">
              {{ ticket.subject }}
            </h5>
            <div class="d-flex gap-2">
              <VChip
                :color="PRIORITY_META[ticket.priority]?.color"
                variant="tonal"
              >
                {{ PRIORITY_META[ticket.priority]?.label ?? ticket.priority }}
              </VChip>
              <VChip
                :color="STATUS_META[ticket.status]?.color"
                variant="tonal"
              >
                {{ STATUS_META[ticket.status]?.label ?? ticket.status }}
              </VChip>
            </div>
          </div>
          <p class="text-body-2 text-medium-emphasis mb-0">
            <template v-if="ticket.category">
              Kategori: {{ CATEGORY_LABELS[ticket.category] }} &middot;
            </template>
            Dibuat oleh {{ ticket.creator?.name ?? '-' }} pada {{ formatDateTime(ticket.created_at) }}
          </p>
        </VCardText>
      </VCard>

      <!-- Riwayat Percakapan -->
      <VCard class="mb-4">
        <VCardTitle class="pa-6 pb-2">
          Riwayat Percakapan
        </VCardTitle>
        <VCardText class="pa-6 pt-2">
          <div
            v-for="msg in conversation"
            :key="msg.id"
            class="conversation-message mb-4"
            :class="msg.is_from_platform_admin ? 'conversation-message--admin' : 'conversation-message--tenant'"
          >
            <div class="d-flex align-center gap-2 mb-1">
              <VChip
                :color="msg.is_from_platform_admin ? 'primary' : 'secondary'"
                size="small"
                variant="tonal"
              >
                <VIcon
                  :icon="msg.is_from_platform_admin ? 'ri-customer-service-2-line' : 'ri-store-2-line'"
                  size="14"
                  start
                />
                {{ msg.is_from_platform_admin ? 'Admin PintuKasir' : (msg.user?.name ?? 'Anda') }}
              </VChip>
              <span
                v-if="msg.isOpening"
                class="text-caption text-medium-emphasis"
              >(tiket awal)</span>
              <span class="text-caption text-medium-emphasis">{{ formatDateTime(msg.created_at) }}</span>
            </div>
            <div class="conversation-bubble">
              {{ msg.message }}
            </div>
          </div>
        </VCardText>
      </VCard>

      <!-- Balas -->
      <VCard v-if="ability.can('create', 'Ticket')">
        <VCardTitle class="pa-6 pb-2">
          Tambah Balasan
        </VCardTitle>
        <VCardText class="pa-6 pt-2">
          <VAlert
            v-if="replyError"
            type="error"
            variant="tonal"
            class="mb-4"
            closable
            @click:close="replyError = ''"
          >
            {{ replyError }}
          </VAlert>

          <VTextarea
            v-model="replyMessage"
            label="Pesan"
            placeholder="Tulis balasan/klarifikasi tambahan Anda di sini"
            rows="3"
            :rules="[requiredValidator]"
          />
        </VCardText>
        <VDivider />
        <VCardActions class="pa-6">
          <VSpacer />
          <VBtn
            color="primary"
            :loading="replyLoading"
            :disabled="!replyMessage.trim()"
            @click="submitReply"
          >
            Kirim Balasan
          </VBtn>
        </VCardActions>
      </VCard>
    </template>
  </div>
</template>

<style scoped>
.conversation-message--admin .conversation-bubble {
  background: rgba(var(--v-theme-primary), 0.08);
  border-inline-start: 3px solid rgb(var(--v-theme-primary));
}

.conversation-message--tenant .conversation-bubble {
  background: rgba(var(--v-theme-on-surface), 0.04);
  border-inline-start: 3px solid rgba(var(--v-theme-on-surface), 0.24);
}

.conversation-bubble {
  padding: 12px 16px;
  border-radius: 8px;
  white-space: pre-line;
}
</style>
